import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Find duplicate values in an array of objects.
 * Returns a Map of index -> first occurrence index for duplicates.
 *
 * @param {Array} items - Array of objects to check
 * @param {Function} getKey - Function to extract the key to check for duplicates
 * @returns {Map} Map of duplicate index -> first occurrence index
 */
function findDuplicateIndices(items, getKey) {
  const seen = new Map(); // key -> first index
  const duplicates = new Map(); // duplicate index -> first index

  items.forEach((item, index) => {
    const key = getKey(item);
    if (!key) return;

    if (seen.has(key)) {
      const firstIndex = seen.get(key);
      duplicates.set(index, firstIndex);
      // Also mark first occurrence as a duplicate (if not already)
      if (!duplicates.has(firstIndex)) {
        duplicates.set(firstIndex, firstIndex);
      }
    } else {
      seen.set(key, index);
    }
  });

  return duplicates;
}

/**
 * Validate field mappings for required fields and duplicates.
 *
 * @param {Array} fieldMappings - Array of field mapping objects
 * @returns {Object} Errors object keyed by index, with field-level errors
 */
function validateFieldMappings(fieldMappings) {
  if (!fieldMappings?.length) return {};

  const errors = {};

  // Find duplicates on destination_field and source_field
  const destDuplicates = findDuplicateIndices(fieldMappings, m => m.destination_field);
  const srcDuplicates = findDuplicateIndices(fieldMappings, m => m.source_field);

  fieldMappings.forEach((mapping, index) => {
    const mappingErrors = {};

    // Check required fields
    if (!mapping.destination_field) {
      mappingErrors.destination_field = 'Destination field is required';
    } else if (destDuplicates.has(index)) {
      const firstIndex = destDuplicates.get(index);
      mappingErrors.destination_field = firstIndex === index
        ? 'Duplicate destination field'
        : `Duplicate destination field (same as row ${firstIndex + 1})`;
    }

    if (!mapping.source_field) {
      mappingErrors.source_field = 'Source field is required';
    } else if (srcDuplicates.has(index)) {
      const firstIndex = srcDuplicates.get(index);
      mappingErrors.source_field = firstIndex === index
        ? 'Duplicate source field'
        : `Duplicate source field (same as row ${firstIndex + 1})`;
    }

    if (Object.keys(mappingErrors).length > 0) {
      errors[index] = mappingErrors;
    }
  });

  return errors;
}

// All 7 mandatory destination fields must be mapped before saving
const REQUIRED_DESTINATION_FIELDS = [
  'Member ID',
  'Client Person ID',
  'Identifier',
  'Subscriber/policyholder ID',
  'Group/employer identifier',
  'Source person ID',
  'Subscriber Family ID',
];

/**
 * Compute save button state from validation errors.
 *
 * @param {Array} fieldMappings - Array of field mapping objects
 * @param {Object} validationErrors - Errors object from validateFieldMappings
 * @returns {Object} Save state with canSave, tooltipMessage, etc.
 */
function computeSaveState(fieldMappings, validationErrors) {
  const isConfigEmpty = !fieldMappings?.length;
  const hasValidationErrors = Object.keys(validationErrors).length > 0;

  // Check which required destination fields are missing
  const mappedDestinations = new Set(
    (fieldMappings || []).map(m => m.destination_field).filter(Boolean)
  );
  const missingRequired = REQUIRED_DESTINATION_FIELDS.filter(
    field => !mappedDestinations.has(field)
  );
  const hasMissingRequired = missingRequired.length > 0;

  const canSave = !isConfigEmpty && !hasValidationErrors && !hasMissingRequired;

  // Categorize errors for tooltip message
  let hasDuplicates = false;
  let hasIncomplete = false;

  for (const errs of Object.values(validationErrors)) {
    if (errs.destination_field?.includes('Duplicate') || errs.source_field?.includes('Duplicate')) {
      hasDuplicates = true;
    }
    if (errs.destination_field?.includes('required') || errs.source_field?.includes('required')) {
      hasIncomplete = true;
    }
  }

  let tooltipMessage = null;
  if (isConfigEmpty) {
    tooltipMessage = 'Configuration is empty - add at least one field mapping';
  } else if (hasMissingRequired) {
    tooltipMessage = `All ${REQUIRED_DESTINATION_FIELDS.length} required destination fields must be mapped: ${missingRequired.join(', ')}`;
  } else if (hasDuplicates && hasIncomplete) {
    tooltipMessage = 'Fix duplicate and incomplete field mappings';
  } else if (hasDuplicates) {
    tooltipMessage = 'Duplicate source or destination fields found';
  } else if (hasValidationErrors) {
    const errorCount = Object.keys(validationErrors).length;
    tooltipMessage = `${errorCount} field mapping${errorCount > 1 ? 's are' : ' is'} incomplete`;
  }

  return { canSave, tooltipMessage, isConfigEmpty, hasValidationErrors };
}

/**
 * Custom hook for managing unsaved changes with navigation warnings.
 *
 * Features:
 * - Tracks dirty state by comparing current config to saved config
 * - Handles browser beforeunload event to warn on close/refresh
 * - Intercepts in-app navigation with window.confirm
 * - Validates field mappings for completeness and duplicates
 *
 * @param {Object} config - Current configuration object
 * @param {Object} savedConfig - Last saved configuration (null if never saved)
 * @param {boolean} isNewDraft - Whether this is a new unsaved draft
 * @returns {Object} Hook state and handlers
 */
export function useUnsavedChanges(config, savedConfig, isNewDraft = false) {
  const location = useLocation();
  const isDirtyRef = useRef(false);

  // Compute dirty state
  const isDirty = useMemo(() => {
    if (isNewDraft) return true;
    if (!savedConfig) return false;
    return JSON.stringify(config) !== JSON.stringify(savedConfig);
  }, [config, savedConfig, isNewDraft]);

  // Keep ref in sync for event handlers
  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  // Validate field mappings
  const validationErrors = useMemo(
    () => validateFieldMappings(config?.field_mappings),
    [config?.field_mappings]
  );

  // Compute save button state
  const saveState = useMemo(
    () => computeSaveState(config?.field_mappings, validationErrors),
    [config?.field_mappings, validationErrors]
  );

  // Handle browser beforeunload event
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // Intercept link clicks for in-app navigation using window.confirm
  useEffect(() => {
    const handleClick = (e) => {
      if (!isDirtyRef.current) return;

      const link = e.target.closest('a');
      if (!link) return;

      const href = link.getAttribute('href');
      if (!href || href.startsWith('http') || href.startsWith('//') || href.startsWith('mailto:')) return;
      if (href === location.pathname) return;

      // eslint-disable-next-line no-restricted-globals
      const shouldLeave = confirm('You have unsaved changes. Leave anyway?');
      if (!shouldLeave) {
        e.preventDefault();
        e.stopPropagation();
      } else {
        isDirtyRef.current = false;
      }
    };

    const handlePopState = () => {
      if (isDirtyRef.current) {
        // eslint-disable-next-line no-restricted-globals
        const shouldLeave = confirm('You have unsaved changes. Leave anyway?');
        if (!shouldLeave) {
          window.history.pushState(null, '', location.pathname);
        }
      }
    };

    document.addEventListener('click', handleClick, true);
    window.addEventListener('popstate', handlePopState);
    window.history.pushState(null, '', location.pathname);

    return () => {
      document.removeEventListener('click', handleClick, true);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [location.pathname]);

  return {
    isDirty,
    validationErrors,
    saveState,
  };
}
